import { isValidReferenceRateForCurrency } from "@/lib/businessRules/referenceRateRule";
import { returnSettlementDateForDays } from "@/lib/businessRules/settlementDateRules";
import { returnSwapLegDefaults } from "@/lib/businessRules/swapConventionsRule";
import { parseTenor, returnDateFromTenor } from "@/lib/businessRules/tenorRule";
import { ISwapSecurity } from "@/lib/interfaces and Enums/ENUMSwapSecurity";
import {
  SWAP_BUSINESS_DAYS,
  SWAP_DAY_COUNTS,
  SWAP_FREQUENCIES,
  TradeInterestType,
} from "@/lib/interfaces and Enums/ENUMSwapTradeMessage";
import {
  ISwapLegTicket,
  ISwapTicket,
  ISwaptionTicket,
} from "@/store/tradeFromScratchStore";
import {
  isInflationDirection,
  returnLegsWithValidReferenceRates,
  returnZcsLegDefaults,
} from "./swapTicketHelper";

// LQC-2939 / LQC-2989 / LQC-2991 - picking a template swap. Templates are sparse (no
// calendar, rarely a reference rate), so a field is only overridden when the template has
// a value we recognise; everything else keeps the currency defaults.

const ZERO_FREQUENCY = "Zero";

export function isZcsTemplate(template: ISwapSecurity): boolean {
  return (
    template.couponFrequency1 === ZERO_FREQUENCY ||
    template.couponFrequency2 === ZERO_FREQUENCY
  );
}

// Spec F19: Buy/Sell Inflation shows only ZCS templates, Pay/Receive hides them. No
// direction yet shows everything.
export function isTemplateAllowedForDirection(
  template: ISwapSecurity,
  direction: string,
): boolean {
  if (!direction) return true;
  return isInflationDirection(direction) === isZcsTemplate(template);
}

// The tenor is the first D/W/M/Y token in the name ("EUR IRS 5Y 6M" -> 5Y). The template's
// own dates are stale, so maturity is re-derived from it.
export function returnTemplateTenor(securityName: string): string {
  const token = securityName.split(/\s+/).find((part) => parseTenor(part) !== null);
  return token ?? "";
}

// Our spelling of a value the template holds in another case ("Modified Following").
function returnKnownOption(value: string | null, options: string[]): string | undefined {
  if (!value) return undefined;
  const lower = value.trim().toLowerCase();
  return options.find((option) => option.toLowerCase() === lower);
}

function returnInterestType(value: string | null, fallback: TradeInterestType) {
  if (value === TradeInterestType.Fixed || value === TradeInterestType.Floating) return value;
  return fallback;
}

export function returnTemplateLeg(
  leg: ISwapLegTicket,
  template: ISwapSecurity,
  legNumber: 1 | 2,
): ISwapLegTicket {
  const pick = <K extends keyof ISwapSecurity>(prefix: string) =>
    template[`${prefix}${legNumber}` as K] as string | null;

  const currency = pick("currency") || leg.currency;
  const interestType = returnInterestType(pick("interestType"), leg.interestType);
  const isFixed = interestType === TradeInterestType.Fixed;
  const base = isZcsTemplate(template)
    ? returnZcsLegDefaults(currency, interestType)
    : returnSwapLegDefaults(currency, interestType);

  const templateRate = pick("referenceRate");
  const referenceRate =
    !isFixed && isValidReferenceRateForCurrency(currency, templateRate)
      ? (templateRate as string)
      : base.referenceRate;

  return {
    ...leg,
    currency,
    interestType,
    couponFrequency: returnKnownOption(pick("couponFrequency"), SWAP_FREQUENCIES) ?? base.couponFrequency,
    resetFrequency: isFixed
      ? base.resetFrequency
      : (returnKnownOption(pick("resetFrequency"), SWAP_FREQUENCIES) ?? base.resetFrequency),
    dayCount: returnKnownOption(pick("dayCount"), SWAP_DAY_COUNTS) ?? base.dayCount,
    businessDay: returnKnownOption(pick("businessDay"), SWAP_BUSINESS_DAYS) ?? base.businessDay,
    calendar: pick("calendar") || base.calendar,
    referenceRate: isFixed ? "" : referenceRate,
    couponRate: isFixed ? leg.couponRate : null,
    spread: isFixed ? null : leg.spread,
  };
}

function returnTemplateMaturity(template: ISwapSecurity, startDate: string): string | undefined {
  const tenor = returnTemplateTenor(template.securityName);
  if (!tenor || !startDate) return undefined;
  return returnDateFromTenor(tenor, startDate) || undefined;
}

// Spec F18. Every local template holds 0, which looks unpopulated rather than T+0, so only a
// positive value moves the dates until that is confirmed (LEG_CREATION_PLAN Q26).
function returnTemplateSettlement(
  template: ISwapSecurity,
  tradeDate: string,
): string | undefined {
  const days = template.settlementDays;
  if (typeof days !== "number" || days <= 0) return undefined;
  return returnSettlementDateForDays(tradeDate, days);
}

export function returnSwapTemplateChanges(
  ticket: ISwapTicket,
  template: ISwapSecurity | undefined,
): Partial<ISwapTicket> {
  if (!template) return { templateName: "" };
  const settlementDate = returnTemplateSettlement(template, ticket.tradeDate);
  const startDate = settlementDate ?? ticket.startDate;
  const maturityDate = returnTemplateMaturity(template, startDate);
  return {
    templateName: template.securityName,
    ...(settlementDate ? { settlementDate, startDate } : {}),
    ...(maturityDate ? { maturityDate } : {}),
    ...(typeof template.cleared === "boolean" ? { cleared: template.cleared } : {}),
    ...returnLegsWithValidReferenceRates({
      leg1: returnTemplateLeg(ticket.leg1, template, 1),
      leg2: returnTemplateLeg(ticket.leg2, template, 2),
    }),
  };
}

// The swaption keeps its own settlement and (mostly uncleared) underlying; the template
// gives the legs, the tenor and, per spec, the Strike from its latest yield.
export function returnSwaptionTemplateChanges(
  ticket: ISwaptionTicket,
  template: ISwapSecurity | undefined,
): Partial<ISwaptionTicket> {
  if (!template) return { templateName: "" };
  const maturityDate = returnTemplateMaturity(template, ticket.startDate);
  return {
    templateName: template.securityName,
    ...(maturityDate ? { maturityDate } : {}),
    ...(typeof template.yield === "number" ? { strike: template.yield } : {}),
    ...returnLegsWithValidReferenceRates({
      leg1: returnTemplateLeg(ticket.leg1, template, 1),
      leg2: returnTemplateLeg(ticket.leg2, template, 2),
    }),
  };
}
